// Etherscan API service - works directly from frontend
// Supports Ethereum Mainnet, Arbitrum One, and ApeChain
// Note: Etherscan V2 API supports multichain (60+ networks including Arbitrum One)

const ETHERSCAN_API_KEY = import.meta.env.VITE_ETHERSCAN_API_KEY || 'Q96NEWBGZXU6C3CSZW8NPM3817PEU3M481';

// Chain-specific Etherscan API endpoints
// Etherscan V2 API supports multichain via chainid parameter
// For Arbitrum, we can also try Arbiscan as fallback if V2 doesn't work
const ETHERSCAN_ENDPOINTS: Record<number, { url: string; useChainId: boolean; chainName: string; fallbackUrl?: string }> = {
  1: { url: 'https://api.etherscan.io/v2/api', useChainId: true, chainName: 'Ethereum Mainnet' },
  42161: { 
    url: 'https://api.etherscan.io/v2/api', 
    useChainId: true, 
    chainName: 'Arbitrum One',
    fallbackUrl: 'https://api.arbiscan.io/api' // Fallback to Arbiscan if V2 fails
  },
  33139: { 
    url: 'https://api.etherscan.io/v2/api', 
    useChainId: true, 
    chainName: 'ApeChain',
    fallbackUrl: 'https://api.apescan.io/api' // Fallback to Apescan if V2 doesn't support ApeChain
  }
};

interface EtherscanResponse {
  status: string;
  message: string;
  result: any;
}

async function fetchWithEndpoint(
  baseUrl: string,
  chainId: number,
  module: string,
  action: string,
  address: string,
  useChainId: boolean,
  params?: Record<string, any>
): Promise<{ success: boolean; data: any[] }> {
  const urlParams = new URLSearchParams({
    module,
    action,
    address,
    startblock: '0',
    endblock: '99999999',
    sort: 'asc',
    apikey: ETHERSCAN_API_KEY,
    ...params
  });

  // Add chainid parameter for V2 API (required for multichain support)
  if (useChainId) {
    urlParams.set('chainid', chainId.toString());
  }

  const url = `${baseUrl}?${urlParams}`;
  
  try {
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorText = await response.text();
      return { success: false, data: [] };
    }
    
    const data: EtherscanResponse = await response.json();
    
    if (data.status === '1' && data.result) {
      if (data.result === '' || data.result === null) {
        return { success: true, data: [] };
      }
      if (Array.isArray(data.result)) {
        const filtered = data.result.filter((r: any) => r !== null && r !== undefined);
        return { success: true, data: filtered };
      }
      return { success: true, data: [data.result] };
    }
    
    // Handle "No transactions found" gracefully
    if (data.message?.includes('No transactions found') || 
        data.message?.includes('No token transfers found') ||
        data.message?.includes('No records found') ||
        (data.message === 'OK' && (data.result === '' || data.result === null || (Array.isArray(data.result) && data.result.length === 0)))) {
      return { success: true, data: [] };
    }
    
    return { success: false, data: [] };
  } catch (error: any) {
    return { success: false, data: [] };
  }
}

export async function fetchEtherscanData(
  chainId: number,
  module: string,
  action: string,
  address: string,
  params?: Record<string, any>
): Promise<any[]> {
  const endpoint = ETHERSCAN_ENDPOINTS[chainId] || ETHERSCAN_ENDPOINTS[1];
  const baseUrl = endpoint.url;
  
  console.log(`[Etherscan API] Fetching ${endpoint.chainName} (${chainId}): ${module}.${action} for ${address.substring(0, 10)}...`);
  
  // Try primary endpoint first
  const result = await fetchWithEndpoint(
    baseUrl,
    chainId,
    module,
    action,
    address,
    endpoint.useChainId,
    params
  );
  
  if (result.success) {
    console.log(`[Etherscan API] ${endpoint.chainName} (${chainId}) - Found ${result.data.length} results for ${module}.${action}`);
    return result.data;
  }
  
  // If primary endpoint failed and we have a fallback, try it
  if (endpoint.fallbackUrl) {
    const fallbackName = chainId === 42161 ? 'Arbiscan' : chainId === 33139 ? 'Apescan' : 'fallback';
    console.log(`[Etherscan API] Primary endpoint failed, trying ${fallbackName} fallback for ${endpoint.chainName}...`);
    const fallbackResult = await fetchWithEndpoint(
      endpoint.fallbackUrl,
      chainId,
      module,
      action,
      address,
      false, // Fallback endpoints typically don't use chainid parameter
      params
    );
    
    if (fallbackResult.success) {
      console.log(`[Etherscan API] ${fallbackName} fallback successful - Found ${fallbackResult.data.length} results for ${module}.${action}`);
      return fallbackResult.data;
    }
    
    console.warn(`[Etherscan API] Both primary and fallback endpoints failed for ${endpoint.chainName}`);
  } else {
    console.warn(`[Etherscan API] ${endpoint.chainName} (${chainId}) - Request failed for ${module}.${action}`);
  }
  
  return [];
}

// Helper function to get native currency symbol based on chain
function getNativeCurrency(chainId: number): string {
  if (chainId === 33139) return 'APE'; // ApeChain
  return 'ETH'; // Ethereum, Arbitrum, etc.
}

// Helper function to format units (for ETH/APE - 18 decimals, for tokens - variable)
function formatUnits(value: bigint | string, decimals: number = 18): number {
  try {
    const divisor = BigInt(10) ** BigInt(decimals);
    const quotient = BigInt(value) / divisor;
    const remainder = BigInt(value) % divisor;
    const decimalPart = Number(remainder) / Number(divisor);
    return Number(quotient) + decimalPart;
  } catch (error) {
    return 0;
  }
}

// Transaction detail interface for bank statement format
interface TransactionDetail {
  date: string;
  time: string;
  timestamp: number;
  type: 'INFLOW' | 'OUTFLOW';
  category: string;
  description: string;
  asset: string;
  amount: number;
  usdValue: number;
  historicalPrice?: number; // Price at time of transaction (for tax purposes)
  transactionHash: string;
  from: string;
  to: string;
  isInternal: boolean;
}

// Process transactions into monthly data structures with individual transaction details
function processTransactions(
  normalTxs: any[],
  internalTxs: any[],
  tokenTxs: any[],
  address: string,
  year: number,
  chainId: number
): {
  monthlyData: Record<string, {
    received: { [key: string]: number };
    spent: { [key: string]: number };
    internalReceived: { [key: string]: number };
    internalSent: { [key: string]: number };
    transactions: TransactionDetail[];
  }>;
  cumulative: {
    received: { [key: string]: number };
    spent: { [key: string]: number };
    internalReceived: { [key: string]: number };
    internalSent: { [key: string]: number };
  };
  allTransactions: TransactionDetail[];
} {
  const addressLower = address.toLowerCase();
  const nativeSymbol = getNativeCurrency(chainId);
  const monthlyData: Record<string, any> = {};
  const allTransactions: TransactionDetail[] = [];
  const cumulative: any = {
    received: {},
    spent: {},
    internalReceived: {},
    internalSent: {}
  };

  // Helper to get or create month data
  const getMonthData = (monthKey: string) => {
    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = {
        received: {},
        spent: {},
        internalReceived: {},
        internalSent: {},
        transactions: []
      };
    }
    return monthlyData[monthKey];
  };

  // Helper to add to cumulative
  const addToCumulative = (category: string, token: string, amount: number) => {
    if (!cumulative[category][token]) {
      cumulative[category][token] = 0;
    }
    cumulative[category][token] += amount;
  };

  // Helper to categorize transaction type - Enhanced for accountant-friendly descriptions
  const categorizeTransaction = (tx: any, type: 'INFLOW' | 'OUTFLOW', asset: string, isInternal: boolean, to?: string, from?: string): string => {
    if (isInternal) {
      return type === 'INFLOW' ? 'Withdrawal/Internal In' : 'Deposit/Internal Out';
    }
    
    // Enhanced categorization based on patterns
    if (type === 'INFLOW') {
      // Inflow categories
      if (asset === nativeSymbol || asset === 'ETH' || asset === 'APE') {
        return 'Funds Received';
      } else if (asset === 'USDC' || asset === 'USDT' || asset === 'DAI') {
        return 'Stablecoin Deposit';
      } else {
        return 'Token Receipt';
      }
    } else {
      // Outflow categories - what things were spent on
      if (asset === nativeSymbol || asset === 'ETH' || asset === 'APE') {
        // Native currency outflows - likely gas or payments
        if (tx.to && tx.to.toLowerCase() !== addressLower) {
          // Could be contract interaction, payment, or transfer
          // Check if 'to' looks like a contract (could enhance with known DEX/DeFi addresses)
          return 'Payment/Transfer';
        }
        return 'Payment';
      } else if (asset === 'USDC' || asset === 'USDT' || asset === 'DAI') {
        return 'Stablecoin Payment';
      } else {
        // Token transfers - could be swaps, payments, etc.
        return 'Token Transfer';
      }
    }
  };

  // Process normal transactions
  for (const tx of normalTxs) {
    if (!tx || !tx.timeStamp) continue;

    try {
      const timestamp = parseInt(tx.timeStamp);
      if (isNaN(timestamp) || timestamp <= 0) continue;

      const dt = new Date(timestamp * 1000);
      if (isNaN(dt.getTime()) || dt.getFullYear() !== year) continue;

      const monthKey = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const monthData = getMonthData(monthKey);

      const value = BigInt(tx.value || '0');
      const ethValue = formatUnits(value, 18);
      if (isNaN(ethValue) || ethValue === 0) continue;

      const from = (tx.from || '').toLowerCase().trim();
      const to = (tx.to || '').toLowerCase().trim();

      if (!from || !to || from === '0x' || to === '0x') continue;

      const dateStr = dt.toISOString().split('T')[0];
      const timeStr = dt.toTimeString().split(' ')[0];

      if (to === addressLower && from !== addressLower && from !== '') {
        if (!monthData.received[nativeSymbol]) monthData.received[nativeSymbol] = 0;
        monthData.received[nativeSymbol] += ethValue;
        addToCumulative('received', nativeSymbol, ethValue);
        
        const detail: TransactionDetail = {
          date: dateStr,
          time: timeStr,
          timestamp,
          type: 'INFLOW',
          category: categorizeTransaction(tx, 'INFLOW', nativeSymbol, false, to, from),
          description: `Received ${ethValue.toFixed(6)} ${nativeSymbol} from ${from.substring(0, 8)}...${from.substring(36)}`,
          asset: nativeSymbol,
          amount: ethValue,
          usdValue: 0, // Will be calculated later with price
          transactionHash: tx.hash || '',
          from,
          to,
          isInternal: false
        };
        monthData.transactions.push(detail);
        allTransactions.push(detail);
      } else if (from === addressLower && to !== addressLower && to !== '') {
        if (!monthData.spent[nativeSymbol]) monthData.spent[nativeSymbol] = 0;
        monthData.spent[nativeSymbol] += ethValue;
        addToCumulative('spent', nativeSymbol, ethValue);
        
        const detail: TransactionDetail = {
          date: dateStr,
          time: timeStr,
          timestamp,
          type: 'OUTFLOW',
          category: categorizeTransaction(tx, 'OUTFLOW', nativeSymbol, false, to, from),
          description: `Sent ${ethValue.toFixed(6)} ${nativeSymbol} to ${to.substring(0, 8)}...${to.substring(36)}`,
          asset: nativeSymbol,
          amount: ethValue,
          usdValue: 0,
          transactionHash: tx.hash || '',
          from,
          to,
          isInternal: false
        };
        monthData.transactions.push(detail);
        allTransactions.push(detail);
      }
    } catch (error) {
      console.warn(`Error processing transaction ${tx.hash || 'unknown'}:`, error);
      continue;
    }
  }

  // Process internal transactions
  for (const tx of internalTxs) {
    if (!tx || !tx.timeStamp) continue;

    try {
      const timestamp = parseInt(tx.timeStamp);
      if (isNaN(timestamp) || timestamp <= 0) continue;

      const dt = new Date(timestamp * 1000);
      if (isNaN(dt.getTime()) || dt.getFullYear() !== year) continue;

      const monthKey = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const monthData = getMonthData(monthKey);

      const value = BigInt(tx.value || '0');
      const ethValue = formatUnits(value, 18);
      if (isNaN(ethValue) || ethValue === 0) continue;

      const from = (tx.from || '').toLowerCase().trim();
      const to = (tx.to || '').toLowerCase().trim();

      if (!from || !to || from === '0x' || to === '0x') continue;

      const dateStr = dt.toISOString().split('T')[0];
      const timeStr = dt.toTimeString().split(' ')[0];

      if (to === addressLower && from !== addressLower && from !== '') {
        if (!monthData.internalReceived[nativeSymbol]) monthData.internalReceived[nativeSymbol] = 0;
        monthData.internalReceived[nativeSymbol] += ethValue;
        addToCumulative('internalReceived', nativeSymbol, ethValue);
        
        const detail: TransactionDetail = {
          date: dateStr,
          time: timeStr,
          timestamp,
          type: 'INFLOW',
          category: 'Internal Transfer In',
          description: `Internal ${nativeSymbol} transfer from contract ${from.substring(0, 6)}...${from.substring(38)}`,
          asset: nativeSymbol,
          amount: ethValue,
          usdValue: 0,
          transactionHash: tx.hash || '',
          from,
          to,
          isInternal: true
        };
        monthData.transactions.push(detail);
        allTransactions.push(detail);
      } else if (from === addressLower && to !== addressLower && to !== '') {
        if (!monthData.internalSent[nativeSymbol]) monthData.internalSent[nativeSymbol] = 0;
        monthData.internalSent[nativeSymbol] += ethValue;
        addToCumulative('internalSent', nativeSymbol, ethValue);
        
        const detail: TransactionDetail = {
          date: dateStr,
          time: timeStr,
          timestamp,
          type: 'OUTFLOW',
          category: 'Internal Transfer Out',
          description: `Internal ${nativeSymbol} transfer to contract ${to.substring(0, 6)}...${to.substring(38)}`,
          asset: nativeSymbol,
          amount: ethValue,
          usdValue: 0,
          transactionHash: tx.hash || '',
          from,
          to,
          isInternal: true
        };
        monthData.transactions.push(detail);
        allTransactions.push(detail);
      }
    } catch (error) {
      console.warn(`Error processing internal transaction:`, error);
      continue;
    }
  }

  // Process token transactions
  for (const transfer of tokenTxs) {
    if (!transfer || !transfer.timeStamp) continue;

    try {
      const timestamp = parseInt(transfer.timeStamp);
      if (isNaN(timestamp) || timestamp <= 0) continue;

      const dt = new Date(timestamp * 1000);
      if (isNaN(dt.getTime()) || dt.getFullYear() !== year) continue;

      const monthKey = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const monthData = getMonthData(monthKey);

      const tokenSymbol = (transfer.tokenSymbol || 'UNKNOWN').trim();
      const value = BigInt(transfer.value || '0');
      const decimals = parseInt(transfer.tokenDecimal || '18');
      if (isNaN(decimals) || decimals < 0 || decimals > 77) continue;

      const tokenValue = formatUnits(value, decimals);
      if (isNaN(tokenValue) || tokenValue === 0) continue;

      const from = (transfer.from || '').toLowerCase().trim();
      const to = (transfer.to || '').toLowerCase().trim();

      if (!from || !to || from === '0x' || to === '0x') continue;

      const dateStr = dt.toISOString().split('T')[0];
      const timeStr = dt.toTimeString().split(' ')[0];

      if (to === addressLower && from !== addressLower && from !== '') {
        if (!monthData.received[tokenSymbol]) monthData.received[tokenSymbol] = 0;
        monthData.received[tokenSymbol] += tokenValue;
        addToCumulative('received', tokenSymbol, tokenValue);
        
        const detail: TransactionDetail = {
          date: dateStr,
          time: timeStr,
          timestamp,
          type: 'INFLOW',
          category: categorizeTransaction(transfer, 'INFLOW', tokenSymbol, false, to, from),
          description: `Received ${tokenValue.toFixed(6)} ${tokenSymbol} from ${from.substring(0, 8)}...${from.substring(36)}`,
          asset: tokenSymbol,
          amount: tokenValue,
          usdValue: 0,
          transactionHash: transfer.hash || '',
          from,
          to,
          isInternal: false
        };
        monthData.transactions.push(detail);
        allTransactions.push(detail);
      } else if (from === addressLower && to !== addressLower && to !== '') {
        if (!monthData.spent[tokenSymbol]) monthData.spent[tokenSymbol] = 0;
        monthData.spent[tokenSymbol] += tokenValue;
        addToCumulative('spent', tokenSymbol, tokenValue);
        
        const detail: TransactionDetail = {
          date: dateStr,
          time: timeStr,
          timestamp,
          type: 'OUTFLOW',
          category: categorizeTransaction(transfer, 'OUTFLOW', tokenSymbol, false, to, from),
          description: `Sent ${tokenValue.toFixed(6)} ${tokenSymbol} to ${to.substring(0, 8)}...${to.substring(36)}`,
          asset: tokenSymbol,
          amount: tokenValue,
          usdValue: 0,
          transactionHash: transfer.hash || '',
          from,
          to,
          isInternal: false
        };
        monthData.transactions.push(detail);
        allTransactions.push(detail);
      }
    } catch (error) {
      console.warn(`Error processing token transaction for ${transfer.tokenSymbol || 'unknown'}:`, error);
      continue;
    }
  }

  // Sort all transactions by timestamp
  allTransactions.sort((a, b) => a.timestamp - b.timestamp);
  
  // Sort transactions within each month
  for (const monthKey in monthlyData) {
    monthlyData[monthKey].transactions.sort((a: TransactionDetail, b: TransactionDetail) => a.timestamp - b.timestamp);
  }

  return { monthlyData, cumulative, allTransactions };
}

export async function analyzeWallet(
  chainId: number,
  address: string,
  year: number
): Promise<{
  normalTxs: any[];
  internalTxs: any[];
  tokenTxs: any[];
  monthlyData: Record<string, any>;
  cumulative: {
    received: { [key: string]: number };
    spent: { [key: string]: number };
    internalReceived: { [key: string]: number };
    internalSent: { [key: string]: number };
  };
  allTransactions: TransactionDetail[];
}> {
  const [normalTxs, internalTxs, tokenTxs] = await Promise.all([
    fetchEtherscanData(chainId, 'account', 'txlist', address),
    fetchEtherscanData(chainId, 'account', 'txlistinternal', address),
    fetchEtherscanData(chainId, 'account', 'tokentx', address)
  ]);

  // Filter transactions by year
  const filterByYear = (txs: any[]) => {
    return txs.filter(tx => {
      if (!tx.timeStamp) return false;
      const timestamp = parseInt(tx.timeStamp);
      const dt = new Date(timestamp * 1000);
      return dt.getFullYear() === year;
    });
  };

  const filteredNormalTxs = filterByYear(normalTxs);
  const filteredInternalTxs = filterByYear(internalTxs);
  const filteredTokenTxs = filterByYear(tokenTxs);

  // Process transactions into monthly data and cumulative totals
  const { monthlyData, cumulative, allTransactions } = processTransactions(
    filteredNormalTxs,
    filteredInternalTxs,
    filteredTokenTxs,
    address,
    year,
    chainId
  );

  return {
    normalTxs: filteredNormalTxs,
    internalTxs: filteredInternalTxs,
    tokenTxs: filteredTokenTxs,
    monthlyData,
    cumulative,
    allTransactions
  };
}

// Token symbol to CoinGecko ID mapping
const TOKEN_IDS: Record<string, string> = {
  'WETH': 'weth',
  'USDC': 'usd-coin',
  'USDT': 'tether',
  'DAI': 'dai',
  'LINK': 'chainlink',
  'UNI': 'uniswap',
  'AAVE': 'aave',
  'MKR': 'maker',
  'COMP': 'compound-governance-token',
  'SNX': 'havven',
  'CRV': 'curve-dao-token',
  'SUSHI': 'sushi',
  '1INCH': '1inch',
  'YFI': 'yearn-finance',
  'BAL': 'balancer',
  'BAT': 'basic-attention-token',
  'ZRX': '0x',
  'ENJ': 'enjincoin',
  'MANA': 'decentraland',
  'SAND': 'the-sandbox',
  'AXS': 'axie-infinity',
  'CHZ': 'chiliz',
  'MATIC': 'matic-network',
  'FTM': 'fantom',
  'AVAX': 'avalanche-2',
  'ATOM': 'cosmos',
  'DOT': 'polkadot',
  'SOL': 'solana',
  'ADA': 'cardano',
  'BNB': 'binancecoin',
  'XRP': 'ripple',
  'DOGE': 'dogecoin',
  'SHIB': 'shiba-inu',
  'PEPE': 'pepe',
  'FLOKI': 'floki',
  'APE': 'apecoin'
};

// Cache for prices
const priceCache = new Map<string, { price: number; timestamp: number }>();
const historicalPriceCache = new Map<string, { price: number }>();
const CACHE_DURATION = 3600000; // 1 hour

// Get historical token price from CoinGecko at specific date, with Perplexity AI fallback
async function getHistoricalTokenPrice(symbol: string, chainId: number, date: Date): Promise<number | null> {
  // Format date as DD-MM-YYYY for CoinGecko API
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  const dateStr = `${dd}-${mm}-${yyyy}`;
  const cacheKey = `${symbol.toUpperCase()}_${chainId}_${dateStr}`;
  
  // Check cache
  const cached = historicalPriceCache.get(cacheKey);
  if (cached) {
    return cached.price;
  }

  try {
    let coinId: string | null = null;

    // For native currencies, use specific IDs
    if (symbol.toUpperCase() === 'ETH' || symbol.toUpperCase() === 'WETH') {
      coinId = 'ethereum';
    } else if (symbol.toUpperCase() === 'APE' && chainId === 33139) {
      coinId = 'apecoin';
    } else {
      // Try token ID mapping
      coinId = TOKEN_IDS[symbol.toUpperCase()] || null;
    }

    if (!coinId) {
      // Try search as fallback
      try {
        const searchResponse = await fetch(`https://api.coingecko.com/api/v3/search?query=${symbol}`);
        const searchData = await searchResponse.json();
        if (searchData.coins && searchData.coins.length > 0) {
          coinId = searchData.coins[0].id;
        }
      } catch (e) {
        // Search failed
      }
    }

    if (coinId) {
      // Use CoinGecko history endpoint
      try {
        const response = await fetch(`https://api.coingecko.com/api/v3/coins/${coinId}/history?date=${dateStr}`);
        const data = await response.json();
        
        if (data.market_data && data.market_data.current_price && data.market_data.current_price.usd) {
          const price = data.market_data.current_price.usd;
          historicalPriceCache.set(cacheKey, { price });
          return price;
        }
      } catch (e) {
        // CoinGecko request failed, will try Perplexity AI
      }
    }

    // If CoinGecko doesn't have the price, try Perplexity AI as fallback
    try {
      console.log(`[getHistoricalTokenPrice] CoinGecko failed for ${symbol} on ${dateStr}, trying Perplexity AI with extended timeout and enhanced parameters...`);
      // Create AbortController with 90 second timeout for Perplexity AI to search multiple APIs for historical FMV
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000);
      
      const perplexityResponse = await fetch('/api/fetch-historical-price', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          symbol,
          date: date.toISOString(),
          chainId
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (perplexityResponse.ok) {
        const perplexityData = await perplexityResponse.json();
        if (perplexityData.price && perplexityData.price > 0) {
          const price = perplexityData.price;
          historicalPriceCache.set(cacheKey, { price });
          console.log(`[getHistoricalTokenPrice] Perplexity AI found price for ${symbol} on ${dateStr}: $${price}`);
          return price;
        } else {
          console.log(`[getHistoricalTokenPrice] Perplexity AI could not find price for ${symbol} on ${dateStr}`);
        }
      } else {
        console.warn(`[getHistoricalTokenPrice] Perplexity AI request failed with status ${perplexityResponse.status}`);
      }
    } catch (perplexityError: any) {
      if (perplexityError.name === 'AbortError') {
        console.warn(`[getHistoricalTokenPrice] Perplexity AI timeout for ${symbol} on ${dateStr} - allowing more time for batch processing`);
      } else {
        console.warn(`[getHistoricalTokenPrice] Perplexity AI fallback failed for ${symbol} on ${dateStr}:`, perplexityError.message);
      }
    }

    return null;
  } catch (error: any) {
    console.warn(`Failed to fetch historical price for ${symbol} on ${dateStr}:`, error.message);
    return null;
  }
}

// Get token price from CoinGecko
async function getTokenPrice(symbol: string, chainId: number): Promise<number | null> {
  const cacheKey = `${symbol.toUpperCase()}_${chainId}`;
  const cached = priceCache.get(cacheKey);
  
  if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
    return cached.price;
  }

  try {
    // For native currencies, use specific IDs
    if (symbol.toUpperCase() === 'ETH' || symbol.toUpperCase() === 'WETH') {
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum,weth&vs_currencies=usd');
      const priceData = await response.json();
      const price = priceData.ethereum?.usd || priceData.weth?.usd;
      if (price) {
        priceCache.set('ETH_' + chainId, { price, timestamp: Date.now() });
        priceCache.set('WETH_' + chainId, { price, timestamp: Date.now() });
        return price;
      }
    } else if (symbol.toUpperCase() === 'APE' && chainId === 33139) {
      // ApeCoin for ApeChain
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=apecoin&vs_currencies=usd');
      const priceData = await response.json();
      const price = priceData.apecoin?.usd;
      if (price) {
        priceCache.set('APE_' + chainId, { price, timestamp: Date.now() });
        return price;
      }
    }

    // Try token ID mapping
    const tokenId = TOKEN_IDS[symbol.toUpperCase()];
    if (tokenId) {
      const response = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${tokenId}&vs_currencies=usd`);
      const priceData = await response.json();
      const price = priceData[tokenId]?.usd;
      if (price) {
        priceCache.set(cacheKey, { price, timestamp: Date.now() });
        return price;
      }
    }

    // Try search as fallback
    try {
      const searchResponse = await fetch(`https://api.coingecko.com/api/v3/search?query=${symbol}`);
      const searchData = await searchResponse.json();
      if (searchData.coins && searchData.coins.length > 0) {
        const coinId = searchData.coins[0].id;
        const priceResponse = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${coinId}&vs_currencies=usd`);
        const priceData = await priceResponse.json();
        const price = priceData[coinId]?.usd;
        if (price) {
          priceCache.set(cacheKey, { price, timestamp: Date.now() });
          return price;
        }
      }
    } catch (e) {
      // Search failed, continue
    }

    return null;
  } catch (error: any) {
    console.warn(`Failed to fetch price for ${symbol}:`, error.message);
    return null;
  }
}

// Format report as comprehensive bank statement - Accountant-friendly format
export async function formatReport(
  walletResults: any[],
  addresses: string[],
  year: number,
  chainId: number
): Promise<string> {
  const nativeCurrency = getNativeCurrency(chainId);
  const chainName = chainId === 1 ? 'Ethereum Mainnet' : chainId === 42161 ? 'Arbitrum One' : chainId === 33139 ? 'ApeChain' : 'Blockchain';
  
  // Fetch native currency price
  let nativePrice = 3000;
  try {
    if (chainId === 33139) {
      // ApeChain - fetch ApeCoin price
      const priceResponse = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=apecoin&vs_currencies=usd');
      if (!priceResponse.ok) {
        throw new Error(`CoinGecko API returned ${priceResponse.status}`);
      }
      const priceData = await priceResponse.json();
      if (priceData && priceData.apecoin && typeof priceData.apecoin.usd === 'number') {
        nativePrice = priceData.apecoin.usd;
        console.log(`[ApeChain] Fetched ApeCoin price: $${nativePrice.toFixed(2)} per APE`);
      } else {
        console.warn('[ApeChain] ApeCoin price not found in response, using default $1.50');
        nativePrice = 1.5;
      }
    } else {
      // Ethereum/Arbitrum - fetch ETH price
      const priceResponse = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum,weth&vs_currencies=usd');
      if (!priceResponse.ok) {
        throw new Error(`CoinGecko API returned ${priceResponse.status}`);
      }
      const priceData = await priceResponse.json();
      if (priceData && (priceData.ethereum || priceData.weth)) {
        nativePrice = priceData.ethereum?.usd || priceData.weth?.usd || 3000;
        console.log(`[${chainName}] Fetched ETH price: $${nativePrice.toFixed(2)} per ETH`);
      } else {
        console.warn(`[${chainName}] ETH price not found, using default $3000`);
        nativePrice = 3000;
      }
    }
  } catch (error: any) {
    console.warn(`Failed to fetch ${nativeCurrency} price for ${chainName}, using default:`, error.message || error);
    // Keep default price based on chain
    if (chainId === 33139) {
      nativePrice = 1.5; // Default ApeCoin price
    } else {
      nativePrice = 3000; // Default ETH price
    }
  }

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];
  
  let report = '';
  
  for (let i = 0; i < walletResults.length; i++) {
    const result = walletResults[i];
    const address = addresses[i];
    
    // Get all transactions and calculate USD values
    const transactions = result.allTransactions || [];
    
    // Calculate USD values for all transactions and fetch historical prices
    console.log(`[formatReport] Processing ${transactions.length} transactions for historical price lookup with enhanced Perplexity AI search...`);
    
    // Process transactions in batches to avoid overwhelming the API
    const BATCH_SIZE = 10;
    let processedCount = 0;
    
    for (let i = 0; i < transactions.length; i += BATCH_SIZE) {
      const batch = transactions.slice(i, Math.min(i + BATCH_SIZE, transactions.length));
      
      await Promise.all(batch.map(async (tx) => {
        // Calculate current USD value
        if (tx.asset === nativeCurrency || 
            (chainId === 33139 && (tx.asset === 'APE' || tx.asset === 'ApeCoin' || tx.asset === 'ape')) ||
            ((chainId === 1 || chainId === 42161) && (tx.asset === 'ETH' || tx.asset === 'Ethereum' || tx.asset === 'WETH'))) {
          tx.usdValue = tx.amount * nativePrice;
          // Debug log for ApeChain transactions
          if (chainId === 33139 && tx.usdValue > 0) {
            console.log(`[ApeChain] Transaction USD value: ${tx.amount.toFixed(6)} APE × $${nativePrice.toFixed(2)} = $${tx.usdValue.toFixed(2)}`);
          }
        } else {
          const tokenPrice = await getTokenPrice(tx.asset, chainId);
          tx.usdValue = tokenPrice ? tx.amount * tokenPrice : 0;
        }
        
        // Fetch historical price at transaction time with enhanced Perplexity AI search
        const txDate = new Date(tx.timestamp * 1000);
        const historicalPrice = await getHistoricalTokenPrice(tx.asset, chainId, txDate);
        tx.historicalPrice = historicalPrice || null;
        
        processedCount++;
        if (processedCount % 10 === 0) {
          console.log(`[formatReport] Processed ${processedCount}/${transactions.length} transactions for historical prices...`);
        }
      }));
      
      // Small delay between batches to avoid rate limiting
      if (i + BATCH_SIZE < transactions.length) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    const successCount = transactions.filter(tx => tx.historicalPrice).length;
    console.log(`[formatReport] Historical prices fetched: ${successCount}/${transactions.length} transactions (${((successCount/transactions.length)*100).toFixed(1)}% success rate)`);
    
    // Professional Report Header
    report += `\n${'='.repeat(100)}\n`;
    report += `                    CRYPTOCURRENCY ACCOUNT STATEMENT\n`;
    report += `                            ${chainName}\n`;
    report += `${'='.repeat(100)}\n\n`;
    report += `Account Number:     ${address}\n`;
    report += `Statement Period:   January 1, ${year} - December 31, ${year}\n`;
    report += `Statement Date:     ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}\n`;
    report += `Network:            ${chainName}\n`;
    report += `Currency:           ${nativeCurrency}\n`;
    report += `Current Price:      1 ${nativeCurrency} = $${nativePrice.toFixed(2)} USD\n`;
    report += `Price Source:       CoinGecko API (Current Market Prices)\n`;
    report += `${'='.repeat(100)}\n\n\n`;
    
    // Check if we have transactions
    if (!transactions || transactions.length === 0) {
      report += `NO TRANSACTIONS FOUND FOR ${year}\n\n`;
      report += `This account had no activity during the specified period.\n`;
      continue;
    }
    
    // Group transactions by month
    const monthlyTransactions: Record<string, TransactionDetail[]> = {};
    for (const tx of transactions) {
      const [yearStr, monthStr] = tx.date.split('-');
      const monthKey = `${yearStr}-${monthStr}`;
      if (!monthlyTransactions[monthKey]) {
        monthlyTransactions[monthKey] = [];
      }
      monthlyTransactions[monthKey].push(tx);
    }
    
    const sortedMonths = Object.keys(monthlyTransactions).sort();
    let runningBalanceUSD = 0;
    let openingBalanceUSD = 0;
    
    // Calculate opening balance (sum of all inflows before first month minus outflows)
    // Note: This is a simplified opening balance - actual would require previous year data
    const firstMonthKey = sortedMonths[0];
    const transactionsBeforeFirstMonth = transactions.filter(tx => {
      const [txYear, txMonth] = tx.date.split('-');
      const txMonthKey = `${txYear}-${txMonth}`;
      return txMonthKey < firstMonthKey;
    });
    
    // Generate monthly statements
    for (let monthIndex = 0; monthIndex < sortedMonths.length; monthIndex++) {
      const monthKey = sortedMonths[monthIndex];
      const [yearStr, monthStr] = monthKey.split('-');
      const monthNum = parseInt(monthStr) - 1;
      const monthName = monthNames[monthNum];
      const monthTxs = monthlyTransactions[monthKey].sort((a, b) => a.timestamp - b.timestamp);
      
      // Set opening balance for this month (ending balance from previous month)
      const monthOpeningBalance = monthIndex === 0 ? openingBalanceUSD : runningBalanceUSD;
      
      // Monthly Statement Header
      report += `\n${'='.repeat(100)}\n`;
      report += `MONTHLY STATEMENT - ${monthName.toUpperCase()} ${yearStr}\n`;
      report += `${'='.repeat(100)}\n\n`;
      
      // Opening balance
      if (monthIndex === 0) {
        report += `Opening Balance: $${openingBalanceUSD.toFixed(2)}\n\n`;
      } else {
        report += `Opening Balance: $${monthOpeningBalance.toFixed(2)} (Brought Forward)\n\n`;
      }
      
      // Transaction Table Header
      report += `Date        Time     Type    Category                    Asset        Amount              USD Value        Balance\n`;
      report += `${'-'.repeat(100)}\n`;
      
      // Initialize month balance with opening balance
      runningBalanceUSD = monthOpeningBalance;
      
      // Process each transaction in chronological order
      for (const tx of monthTxs) {
        const dateShort = tx.date.split('-').reverse().join('/'); // DD/MM/YYYY
        const timeShort = tx.time.substring(0, 5); // HH:MM
        const type = tx.type === 'INFLOW' ? 'IN' : 'OUT';
        const category = tx.category.padEnd(28);
        const asset = tx.asset.padEnd(11);
        
        // Add +/- prefix based on transaction type
        const signPrefix = tx.type === 'INFLOW' ? '+' : '-';
        const amount = tx.amount >= 0.01 ? tx.amount.toFixed(6) : tx.amount.toFixed(12);
        const amountStr = `${signPrefix}${amount}`.padStart(23);
        
        // Use historical price if available, otherwise current price
        const priceForDisplay = tx.historicalPrice || (tx.usdValue && tx.amount > 0 ? tx.usdValue / tx.amount : null);
        const usdValue = tx.usdValue > 0 ? `${signPrefix}$${tx.usdValue.toFixed(2)}` : 'Price N/A';
        const usdValueStr = usdValue.padStart(19);
        
        // Update running balance
        if (tx.type === 'INFLOW' && tx.usdValue > 0) {
          runningBalanceUSD += tx.usdValue;
        } else if (tx.type === 'OUTFLOW' && tx.usdValue > 0) {
          runningBalanceUSD -= tx.usdValue;
        }
        
        const balance = `$${runningBalanceUSD.toFixed(2)}`.padStart(15);
        
        report += `${dateShort.padEnd(12)} ${timeShort.padEnd(8)} ${type.padEnd(6)} ${category.substring(0, 25).padEnd(26)} ${asset.padEnd(12)} ${amountStr.padStart(18)} ${usdValueStr.padStart(16)} ${balance.padStart(15)}\n`;
        
        // Add transaction hash on next line (more compact)
        if (tx.transactionHash) {
          report += `            Hash: ${tx.transactionHash.substring(0, 66)}\n`;
        }
      }
      
      // Monthly Summary
      const monthInflows = monthTxs.filter(tx => tx.type === 'INFLOW').reduce((sum, tx) => sum + (tx.usdValue || 0), 0);
      const monthOutflows = monthTxs.filter(tx => tx.type === 'OUTFLOW').reduce((sum, tx) => sum + (tx.usdValue || 0), 0);
      const monthNetFlow = monthInflows - monthOutflows;
      
      report += `${'-'.repeat(100)}\n`;
      report += `MONTHLY SUMMARY - ${monthName.toUpperCase()} ${yearStr}\n`;
      report += `${'-'.repeat(100)}\n`;
      report += `Opening Balance:        $${monthOpeningBalance.toFixed(2)}\n`;
      report += `Total Inflows:          $${monthInflows.toFixed(2)}\n`;
      report += `Total Outflows:         $${monthOutflows.toFixed(2)}\n`;
      report += `Net Flow:               $${monthNetFlow.toFixed(2)} ${monthNetFlow >= 0 ? '(Net Gain)' : '(Net Loss)'}\n`;
      report += `Ending Balance:         $${runningBalanceUSD.toFixed(2)}\n`;
      report += `${'='.repeat(100)}\n\n`;
    }
    
    // Annual Summary
    report += `\n${'='.repeat(100)}\n`;
    report += `ANNUAL SUMMARY - ${year}\n`;
    report += `${'='.repeat(100)}\n\n`;
    
    // Calculate annual totals
    const annualInflows = transactions.filter(tx => tx.type === 'INFLOW').reduce((sum, tx) => sum + (tx.usdValue || 0), 0);
    const annualOutflows = transactions.filter(tx => tx.type === 'OUTFLOW').reduce((sum, tx) => sum + (tx.usdValue || 0), 0);
    const annualNetFlow = annualInflows - annualOutflows;
    
    report += `ANNUAL TOTALS:\n`;
    report += `${'-'.repeat(100)}\n`;
    report += `Total Inflows:            $${annualInflows.toFixed(2)}\n`;
    report += `Total Outflows:           $${annualOutflows.toFixed(2)}\n`;
    report += `Net Cash Flow:            $${annualNetFlow.toFixed(2)} ${annualNetFlow >= 0 ? '(Net Gain)' : '(Net Loss)'}\n`;
    report += `Total Transactions:       ${transactions.length}\n\n`;
    
    // Asset breakdown
    const assetBreakdown: Record<string, { inflows: number; outflows: number; net: number; transactions: number }> = {};
    for (const tx of transactions) {
      if (!assetBreakdown[tx.asset]) {
        assetBreakdown[tx.asset] = { inflows: 0, outflows: 0, net: 0, transactions: 0 };
      }
      assetBreakdown[tx.asset].transactions++;
      if (tx.type === 'INFLOW') {
        assetBreakdown[tx.asset].inflows += tx.usdValue || 0;
      } else {
        assetBreakdown[tx.asset].outflows += tx.usdValue || 0;
      }
      assetBreakdown[tx.asset].net = assetBreakdown[tx.asset].inflows - assetBreakdown[tx.asset].outflows;
    }
    
    report += `ASSET BREAKDOWN:\n`;
    report += `${'-'.repeat(100)}\n`;
    report += `Asset           Inflows (USD)      Outflows (USD)     Net Flow (USD)      Transactions\n`;
    report += `${'-'.repeat(100)}\n`;
    
    const sortedAssets = Object.keys(assetBreakdown).sort((a, b) => {
      const netA = assetBreakdown[a].net;
      const netB = assetBreakdown[b].net;
      return Math.abs(netB) - Math.abs(netA);
    });
    
    for (const asset of sortedAssets) {
      const breakdown = assetBreakdown[asset];
      report += `${asset.padEnd(15)} $${breakdown.inflows.toFixed(2).padStart(15)} $${breakdown.outflows.toFixed(2).padStart(15)} $${breakdown.net.toFixed(2).padStart(15)} ${breakdown.transactions.toString().padStart(15)}\n`;
    }
    
    // Transaction category breakdown
    const categoryBreakdown: Record<string, { inflows: number; outflows: number; net: number; transactions: number }> = {};
    for (const tx of transactions) {
      if (!categoryBreakdown[tx.category]) {
        categoryBreakdown[tx.category] = { inflows: 0, outflows: 0, net: 0, transactions: 0 };
      }
      categoryBreakdown[tx.category].transactions++;
      if (tx.type === 'INFLOW') {
        categoryBreakdown[tx.category].inflows += tx.usdValue || 0;
      } else {
        categoryBreakdown[tx.category].outflows += tx.usdValue || 0;
      }
      categoryBreakdown[tx.category].net = categoryBreakdown[tx.category].inflows - categoryBreakdown[tx.category].outflows;
    }
    
    report += `\n\nTRANSACTION CATEGORY BREAKDOWN:\n`;
    report += `${'-'.repeat(100)}\n`;
    report += `Category                    Inflows (USD)      Outflows (USD)     Net Flow (USD)      Transactions\n`;
    report += `${'-'.repeat(100)}\n`;
    
    const sortedCategories = Object.keys(categoryBreakdown).sort((a, b) => {
      const netA = categoryBreakdown[a].net;
      const netB = categoryBreakdown[b].net;
      return Math.abs(netB) - Math.abs(netA);
    });
    
    for (const category of sortedCategories) {
      const breakdown = categoryBreakdown[category];
      report += `${category.padEnd(28)} $${breakdown.inflows.toFixed(2).padStart(15)} $${breakdown.outflows.toFixed(2).padStart(15)} $${breakdown.net.toFixed(2).padStart(15)} ${breakdown.transactions.toString().padStart(15)}\n`;
    }
    
    // Spending analysis
    report += `\n\nSPENDING ANALYSIS:\n`;
    report += `${'-'.repeat(100)}\n`;
    
    const inflowTransactions = transactions.filter(tx => tx.type === 'INFLOW');
    const outflowTransactions = transactions.filter(tx => tx.type === 'OUTFLOW');
    const avgInflowSize = inflowTransactions.length > 0 ? annualInflows / inflowTransactions.length : 0;
    const avgOutflowSize = outflowTransactions.length > 0 ? annualOutflows / outflowTransactions.length : 0;
    const largestInflow = inflowTransactions.length > 0 ? Math.max(...inflowTransactions.map(tx => tx.usdValue || 0)) : 0;
    const largestOutflow = outflowTransactions.length > 0 ? Math.max(...outflowTransactions.map(tx => tx.usdValue || 0)) : 0;
    
    report += `Total Inflow Transactions:   ${inflowTransactions.length}\n`;
    report += `Total Outflow Transactions:  ${outflowTransactions.length}\n`;
    report += `Average Inflow Size:         $${avgInflowSize.toFixed(2)}\n`;
    report += `Average Outflow Size:        $${avgOutflowSize.toFixed(2)}\n`;
    report += `Largest Single Inflow:       $${largestInflow.toFixed(2)}\n`;
    report += `Largest Single Outflow:      $${largestOutflow.toFixed(2)}\n`;
    
    // Net flow analysis - recalculate monthly net flows for summary
    const monthlyNetFlowsForSummary: Record<string, number> = {};
    for (const monthKey of sortedMonths) {
      const monthTxs = monthlyTransactions[monthKey] || [];
      const monthIn = monthTxs.filter(tx => tx.type === 'INFLOW').reduce((sum, tx) => sum + (tx.usdValue || 0), 0);
      const monthOut = monthTxs.filter(tx => tx.type === 'OUTFLOW').reduce((sum, tx) => sum + (tx.usdValue || 0), 0);
      monthlyNetFlowsForSummary[monthKey] = monthIn - monthOut;
    }
    
    if (sortedMonths.length > 0 && Object.keys(monthlyNetFlowsForSummary).length > 0) {
      const bestMonth = Object.keys(monthlyNetFlowsForSummary).reduce((a, b) => monthlyNetFlowsForSummary[a] > monthlyNetFlowsForSummary[b] ? a : b, sortedMonths[0]);
      const worstMonth = Object.keys(monthlyNetFlowsForSummary).reduce((a, b) => monthlyNetFlowsForSummary[a] < monthlyNetFlowsForSummary[b] ? a : b, sortedMonths[0]);
      const [bestYear, bestMonthStr] = bestMonth.split('-');
      const [worstYear, worstMonthStr] = worstMonth.split('-');
      const bestMonthName = monthNames[parseInt(bestMonthStr) - 1];
      const worstMonthName = monthNames[parseInt(worstMonthStr) - 1];
      
      report += `\nBest Performing Month:      ${bestMonthName} ${bestYear} (Net: $${monthlyNetFlowsForSummary[bestMonth].toFixed(2)})\n`;
      report += `Worst Performing Month:     ${worstMonthName} ${worstYear} (Net: $${monthlyNetFlowsForSummary[worstMonth].toFixed(2)})\n`;
    }
    
    // Tax Extension - Asset Values at Transaction Time (Bank Statement Format)
    report += `\n${'='.repeat(100)}\n`;
    report += `TAX COMPLIANCE SECTION - FAIR MARKET VALUE AT TRANSACTION TIME\n`;
    report += `${'='.repeat(100)}\n\n`;
    report += `This section provides transaction-level detail for tax reporting purposes.\n`;
    report += `All values represent Fair Market Value (FMV) in USD at the time of each transaction.\n`;
    report += `FMV Calculation: Quantity × Historical Price per Unit = Total FMV (USD)\n`;
    report += `Use this data for capital gains/losses calculations and tax filing.\n\n`;
    
    // Group by asset for better tax reporting
    const assetGroups: Record<string, TransactionDetail[]> = {};
    for (const tx of transactions) {
      if (!assetGroups[tx.asset]) {
        assetGroups[tx.asset] = [];
      }
      assetGroups[tx.asset].push(tx);
    }
    
    const sortedAssetsForTax = Object.keys(assetGroups).sort();
    
    for (const asset of sortedAssetsForTax) {
      const assetTxs = assetGroups[asset].sort((a, b) => a.timestamp - b.timestamp);
      
      report += `${'='.repeat(100)}\n`;
      report += `ASSET: ${asset.toUpperCase()}\n`;
      report += `${'='.repeat(100)}\n\n`;
      
      // Transaction table header (similar to bank statement format)
      report += `Date        Time     Type    Category                    Currency    Quantity              FMV/Unit (USD)    Total FMV (USD)    Transaction Hash\n`;
      report += `${'-'.repeat(100)}\n`;
      
      let totalCostBasis = 0;
      let totalProceeds = 0;
      let totalQuantityIn = 0;
      let totalQuantityOut = 0;
      
      for (const tx of assetTxs) {
        const dateShort = tx.date.split('-').reverse().join('/'); // DD/MM/YYYY
        const timeShort = tx.time.substring(0, 5); // HH:MM
        const type = tx.type === 'INFLOW' ? 'IN' : 'OUT';
        const category = tx.category.substring(0, 25).padEnd(28);
        const currency = asset.padEnd(10);
        
        // Quantity with sign
        const signPrefix = tx.type === 'INFLOW' ? '+' : '-';
        const quantity = tx.amount >= 0.01 ? tx.amount.toFixed(6) : tx.amount.toFixed(12);
        const quantityStr = `${signPrefix}${quantity}`.padStart(20);
        
        // Historical price at time of transaction (FMV per unit)
        const priceAtTime = tx.historicalPrice ? tx.historicalPrice : null;
        const priceAtTimeStr = priceAtTime ? `$${priceAtTime.toFixed(2)}`.padStart(18) : 'FMV Unavailable'.padStart(18);
        
        // Total FMV at time of transaction (only if historical price available)
        // Calculation: Quantity × FMV per Unit = Total FMV
        const totalFMV = priceAtTime ? tx.amount * priceAtTime : 0;
        const totalFMVStr = totalFMV > 0 ? `${signPrefix}$${totalFMV.toFixed(2)}`.padStart(18) : 'N/A'.padStart(18);
        
        const txHash = tx.transactionHash ? tx.transactionHash.substring(0, 20) + '...' : 'N/A';
        
        report += `${dateShort.padEnd(12)} ${timeShort.padEnd(8)} ${type.padEnd(6)} ${category} ${currency} ${quantityStr.padStart(20)} ${priceAtTimeStr.padStart(18)} ${totalFMVStr.padStart(18)} ${txHash}\n`;
        
        // Show calculation for transactions with FMV
        if (priceAtTime && totalFMV > 0) {
          report += `            Calculation: ${tx.amount.toFixed(6)} ${asset} × $${priceAtTime.toFixed(2)} = $${totalFMV.toFixed(2)} USD\n`;
        }
        
        // Track totals for summary (only if historical price available)
        if (priceAtTime) {
          if (tx.type === 'INFLOW') {
            totalCostBasis += totalFMV;
            totalQuantityIn += tx.amount;
          } else {
            totalProceeds += totalFMV;
            totalQuantityOut += tx.amount;
          }
        }
      }
      
      // Asset summary (similar to monthly summary format)
      const netQuantity = totalQuantityIn - totalQuantityOut;
      const netGainLoss = totalProceeds - totalCostBasis;
      
      report += `${'-'.repeat(100)}\n`;
      report += `ASSET SUMMARY - ${asset.toUpperCase()}:\n`;
      report += `${'-'.repeat(100)}\n`;
      report += `Total Acquired:          ${totalQuantityIn.toFixed(8)} ${asset}\n`;
      report += `Cost Basis (USD):        $${totalCostBasis.toFixed(2)}\n`;
      report += `Total Disposed:          ${totalQuantityOut.toFixed(8)} ${asset}\n`;
      report += `Proceeds (USD):          $${totalProceeds.toFixed(2)}\n`;
      report += `Net Quantity:            ${netQuantity >= 0 ? '+' : ''}${netQuantity.toFixed(8)} ${asset}\n`;
      report += `Realized Gain/Loss (USD): ${netGainLoss >= 0 ? '+' : ''}$${netGainLoss.toFixed(2)}\n`;
      report += `${'='.repeat(100)}\n\n`;
    }
    
    // Overall tax summary
    report += `\n${'='.repeat(100)}\n`;
    report += `TAX SUMMARY - ALL TRANSACTIONS\n`;
    report += `${'='.repeat(100)}\n\n`;
    
    let totalAcquisitions = 0;
    let totalDispositions = 0;
    let totalCostBasisAll = 0;
    let totalProceedsAll = 0;
    
    for (const tx of transactions) {
      const priceAtTime = tx.historicalPrice || null;
      const totalFMV = priceAtTime ? tx.amount * priceAtTime : 0;
      
      if (totalFMV > 0) {
        if (tx.type === 'INFLOW') {
          totalAcquisitions += totalFMV;
          totalCostBasisAll += totalFMV;
        } else {
          totalDispositions += totalFMV;
          totalProceedsAll += totalFMV;
        }
      }
    }
    
    const netGainLossAll = totalProceedsAll - totalCostBasisAll;
    
    report += `Total Cost Basis (Acquisitions):    $${totalCostBasisAll.toFixed(2)}\n`;
    report += `Total Proceeds (Dispositions):      $${totalProceedsAll.toFixed(2)}\n`;
    report += `Net Realized Gain/Loss:             ${netGainLossAll >= 0 ? '+' : ''}$${netGainLossAll.toFixed(2)}\n`;
    report += `\nNote: Unrealized gains/losses are calculated based on current market value vs. cost basis.\n`;
    report += `      Only realized transactions (dispositions) are reportable for tax purposes.\n`;
    
    report += `\n${'-'.repeat(100)}\n`;
    report += `IMPORTANT TAX NOTES:\n`;
    report += `${'-'.repeat(100)}\n`;
    report += `1. Fair Market Value (FMV) shown represents the USD value at transaction time\n`;
    report += `2. FMV Calculation: Quantity × Historical Price per Unit = Total FMV (USD)\n`;
    report += `3. Historical prices are sourced from CoinGecko API with AI-assisted verification (60s timeout)\n`;
    report += `4. Currency: All FMV values are displayed in USD (United States Dollars)\n`;
    report += `5. Cost basis = Total value of acquisitions (inflows) in USD\n`;
    report += `6. Proceeds = Total value of dispositions (outflows) in USD\n`;
    report += `7. Realized Gain/Loss = Proceeds (USD) - Cost Basis (USD) for disposed assets\n`;
    report += `8. This report is for informational purposes only\n`;
    report += `9. Consult with a qualified tax professional for tax filing requirements\n`;
    report += `10. Some transactions may show "FMV Unavailable" if historical price data could not be determined\n`;
    report += `11. Maintain supporting documentation (transaction hashes) for all transactions\n`;
    report += `${'-'.repeat(100)}\n\n`;
    
    report += `\n${'='.repeat(100)}\n`;
    report += `END OF STATEMENT\n`;
    report += `Generated: ${new Date().toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}\n`;
    report += `Data Source: Etherscan API | Price Source: CoinGecko API\n`;
    report += `Network: ${chainName} | Currency: ${nativeCurrency}\n`;
    report += `${'='.repeat(100)}\n\n`;
  }
  
  return report;
}